import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Avatar,
  Typography,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  PhotoCamera as CameraIcon,
} from '@mui/icons-material';
import { message } from 'antd';

interface ImageUploadProps {
  currentImage?: string;
  onUpload: (file: File) => Promise<any>;
  onDelete?: () => Promise<void>;
  label?: string;
  type?: 'avatar' | 'product' | 'equipment';
  maxSize?: number; // en MB
  disabled?: boolean;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  currentImage,
  onUpload,
  onDelete,
  label = 'Télécharger une image',
  type = 'product',
  maxSize = 5,
  disabled = false,
}) => {
  const [preview, setPreview] = useState<string | null>(currentImage || null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (file: File) => {
    // Validation de la taille
    if (file.size > maxSize * 1024 * 1024) {
      message.error(`La taille du fichier ne doit pas dépasser ${maxSize}MB`);
      return;
    }

    // Validation du type
    if (!file.type.startsWith('image/')) {
      message.error('Seuls les fichiers images sont autorisés');
      return;
    }

    // Prévisualisation
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload
    setUploading(true);
    try {
      await onUpload(file);
      message.success('Image uploadée avec succès');
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Erreur lors de l\'upload');
      setPreview(currentImage || null);
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);

    const file = event.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDelete = async () => {
    if (onDelete) {
      setUploading(true);
      try {
        await onDelete();
        setPreview(null);
        message.success('Image supprimée avec succès');
      } catch (error) {
        message.error('Erreur lors de la suppression');
      } finally {
        setUploading(false);
      }
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>

      <Paper
        elevation={dragOver ? 8 : 2}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          p: 3,
          textAlign: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          border: dragOver ? '2px dashed #0a543d' : '2px dashed #ddd',
          backgroundColor: dragOver ? 'rgba(10, 84, 61, 0.05)' : 'transparent',
          transition: 'all 0.3s',
          position: 'relative',
        }}
      >
        {uploading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              zIndex: 1,
            }}
          >
            <CircularProgress sx={{ color: '#0a543d' }} />
          </Box>
        )}

        {preview ? (
          <Box>
            {type === 'avatar' ? (
              <Avatar
                src={preview}
                sx={{
                  width: 120,
                  height: 120,
                  margin: '0 auto',
                  mb: 2,
                }}
              />
            ) : (
              <img
                src={preview}
                alt="Preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: 300,
                  objectFit: 'contain',
                  marginBottom: 16,
                }}
              />
            )}

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<CameraIcon />}
                onClick={handleClick}
                disabled={disabled || uploading}
                sx={{
                  color: '#0a543d',
                  borderColor: '#0a543d',
                  '&:hover': {
                    borderColor: '#0a543d',
                    backgroundColor: 'rgba(10, 84, 61, 0.05)',
                  },
                }}
              >
                Changer
              </Button>

              {onDelete && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDelete}
                  disabled={disabled || uploading}
                >
                  Supprimer
                </Button>
              )}
            </Box>
          </Box>
        ) : (
          <Box onClick={handleClick}>
            <UploadIcon
              sx={{
                fontSize: 64,
                color: '#0a543d',
                mb: 2,
              }}
            />
            <Typography variant="body1" gutterBottom>
              Glissez-déposez une image ici
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              ou
            </Typography>
            <Button
              variant="contained"
              disabled={disabled || uploading}
              sx={{
                mt: 1,
                backgroundColor: '#0a543d',
                '&:hover': {
                  backgroundColor: '#083d2f',
                },
              }}
            >
              Parcourir
            </Button>
            <Typography variant="caption" display="block" sx={{ mt: 2 }} color="text.secondary">
              Formats acceptés: JPEG, PNG, GIF, WEBP (max {maxSize}MB)
            </Typography>
          </Box>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: 'none' }}
          disabled={disabled || uploading}
        />
      </Paper>
    </Box>
  );
};

export default ImageUpload;
